/**
 * Core i18n functionality
 */
import { en } from "./locales/en";
import { ms } from "./locales/ms";
const translations = {
    en,
    ms,
};
const defaultConfig = {
    defaultLocale: "ms", // Bahasa Malaysia as primary per project requirements
    fallbackLocale: "en",
    supportedLocales: ["en", "ms"],
};
/**
 * Get a nested value from an object using dot notation
 */
function getNestedValue(obj, path) {
    const keys = path.split(".");
    let current = obj;
    for (const key of keys) {
        if (current && typeof current === "object" && key in current) {
            current = current[key];
        }
        else {
            return path; // Return the key if not found
        }
    }
    return typeof current === "string" ? current : path;
}
/**
 * Replace placeholders in a string with values
 */
function replacePlaceholders(text, options) {
    if (!options)
        return text;
    return Object.keys(options).reduce((acc, key) => {
        const placeholder = `{{${key}}}`;
        return acc.replace(new RegExp(placeholder, "g"), String(options[key]));
    }, text);
}
/**
 * Translate a key to the current locale
 */
export function translate(key, locale = defaultConfig.defaultLocale, options) {
    const localeData = translations[locale] || translations[defaultConfig.fallbackLocale];
    const text = getNestedValue(localeData, key);
    return replacePlaceholders(text, options);
}
/**
 * Get translation function for a specific locale
 */
export function getTranslator(locale = defaultConfig.defaultLocale) {
    return (key, options) => translate(key, locale, options);
}
/**
 * Check if a locale is supported
 */
export function isSupportedLocale(locale) {
    return defaultConfig.supportedLocales.includes(locale);
}
/**
 * Get the display name for a locale
 */
export function getLocaleDisplayName(locale) {
    const names = {
        en: "English",
        ms: "Bahasa Malaysia",
    };
    return names[locale];
}
/**
 * Get prayer time name in the specified locale
 */
export function getPrayerName(prayer, locale = defaultConfig.defaultLocale) {
    const t = getTranslator(locale);
    const prayerMap = {
        fajr: "prayer.fajr",
        sunrise: "prayer.sunrise",
        dhuhr: "prayer.dhuhr",
        asr: "prayer.asr",
        maghrib: "prayer.maghrib",
        isha: "prayer.isha",
    };
    return t(prayerMap[prayer]);
}
/**
 * Get all prayer names in order
 */
export function getAllPrayerNames(locale = defaultConfig.defaultLocale) {
    return {
        fajr: getPrayerName("fajr", locale),
        sunrise: getPrayerName("sunrise", locale),
        dhuhr: getPrayerName("dhuhr", locale),
        asr: getPrayerName("asr", locale),
        maghrib: getPrayerName("maghrib", locale),
        isha: getPrayerName("isha", locale),
    };
}
/**
 * Format date according to locale
 */
export function formatDate(date, locale = defaultConfig.defaultLocale) {
    const localeCode = locale === "ms" ? "ms-MY" : "en-MY";
    return date.toLocaleDateString(localeCode, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}
/**
 * Format time according to locale
 */
export function formatTime(date, locale = defaultConfig.defaultLocale) {
    const localeCode = locale === "ms" ? "ms-MY" : "en-MY";
    return date.toLocaleTimeString(localeCode, {
        hour: "2-digit",
        minute: "2-digit",
    });
}
/**
 * Format date and time according to locale
 */
export function formatDateTime(date, locale = defaultConfig.defaultLocale) {
    const localeCode = locale === "ms" ? "ms-MY" : "en-MY";
    return date.toLocaleString(localeCode, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}
//# sourceMappingURL=i18n.js.map